import { isAndroid, isIOS, isMobile } from 'react-device-detect'
import { isReactNativeWebView } from './react-native'

const isBrowser =
  typeof window !== 'undefined' && typeof document !== 'undefined'

const amplitude = isBrowser ? require('@amplitude/analytics-browser') : null

class AnalyticsManager {
  private _instance
  private _identify
  private _appendEventProps
  // Values supplied before init fires (e.g. Layout's useEffect runs once on
  // mount, before CurrentUserProvider's async `me` query resolves). Stashed
  // here and applied inside _initializeForIdentifiedUser so cross-platform
  // deviceId stitching and UTM attribution survive the lazy-init delay.
  private _pendingDeviceId
  private _pendingUserProperties = {}

  constructor() {
    if (!this._instance) {
      this._instance = this

      if (isMobile) {
        this._appendEventProps = {
          '[Kickoff] OS': isIOS ? 'iOS' : isAndroid ? 'Android' : null,
          '[Kickoff] Platform': isReactNativeWebView()
            ? 'Mobile Webview'
            : 'Web',
        }
      } else {
        this._appendEventProps = {
          '[Kickoff] OS': 'Desktop',
          '[Kickoff] Platform': 'Web',
        }
      }
    }

    return this._instance
  }

  // Surfaces we actually want Amplitude tracking on. Everything else (landing
  // pages, signup funnel, coach onboarding, payment, etc.) stays uninitialized
  // so it doesn't count toward MTU. Path-gating applies inside the mobile
  // webview too — the client-sign-up survey runs in a WebView at /client-sign-up
  // and must not count signup users as MTU.
  _isTrackedSurface() {
    if (!isBrowser) return false
    const path = window.location.pathname
    return path.startsWith('/dashboard') || path.startsWith('/meet')
  }

  // Lazily initialize Amplitude. Gated on both an identified user AND a
  // tracked surface — every other method no-ops until both are true.
  _initializeForIdentifiedUser() {
    if (!isBrowser || this._identify) return
    if (!this._isTrackedSurface()) return
    const initOptions: any = {
      defaultTracking: false,
      minIdLength: 1,
    }
    // Apply a deviceId queued before init (e.g. from `?deviceId=` in a mobile
    // webview link) by passing it into init options so the first identify
    // call already uses the stitched ID rather than an auto-generated one.
    if (this._pendingDeviceId) {
      initOptions.deviceId = this._pendingDeviceId
      this._pendingDeviceId = undefined
    }
    amplitude.init(process.env.AMPLITUDE_SDK_API_KEY, null, initOptions)
    this._identify = new amplitude.Identify()
    // Drain any user properties queued before init.
    const pending = this._pendingUserProperties
    this._pendingUserProperties = {}
    Object.keys(pending).forEach(key => {
      this._identify.set(key, pending[key])
    })
  }

  logEvent(name: string, properties: object = {}) {
    if (!this._identify) return
    amplitude.track(name, { ...properties, ...this._appendEventProps })
  }

  identifyUser({ me }) {
    if (!me?.user) return
    this._initializeForIdentifiedUser()
    if (!this._identify) return

    const { id } = me.user

    if (me.user.coach) {
      const coach = me.user.coach
      this._identify.set('coachId', coach.id)
      this._identify.set('coachLevel', coach.levelId)
      this._identify.set('userType', 'coach')
    } else {
      this._identify.set('userType', 'client')
    }

    // Do not send email/name/phone — these are PHI when tied to a client.
    amplitude.setUserId(id)
    amplitude.identify(this._identify)
  }

  setUserProperties(properties: object) {
    if (!this._identify) {
      Object.assign(this._pendingUserProperties, properties)
      return
    }
    Object.keys(properties).forEach(key => {
      this._identify.set(key, properties[key])
    })
    amplitude.identify(this._identify)
  }

  setDeviceId(deviceId) {
    if (!this._identify) {
      this._pendingDeviceId = deviceId
      return
    }
    amplitude.setDeviceId(deviceId)
  }
}

const instance = new AnalyticsManager()

export default instance
